import { Badge } from '@staff-app/ui';
import { subscriptionBadge } from '@/features/customers/components/CustomerFacts';
import { accountStatus } from '@/features/customers/lib/customer-info';
import { dayLabel } from '@/features/optihub/components/DunningExclusionDialog';

/**
 * The Account status + Subscription status badges for the Dunning Review page's tables — the
 * SAME badges the customer page shows (CustomerFacts), so a status reads the same everywhere.
 * Shared by the review queue and the Dunning Exclusion List so the two cannot drift.
 */

/** payment.isTrial + isActive → Trial / Trial end / Account activated / Account deactivated. */
export function AccountStatusBadge({
  hasAccount,
  isActive,
  isTrial,
  testId,
}: {
  /** False when no OptiSigns account links to the row — shows "—". */
  hasAccount: boolean;
  isActive?: boolean | null;
  isTrial?: boolean | null;
  testId?: string;
}) {
  if (!hasAccount) {
    return (
      <span className="text-muted-foreground" data-testid={testId}>
        {'—'}
      </span>
    );
  }
  const a = accountStatus(isTrial ?? undefined, isActive ?? undefined);
  // The column is already headed "Account status", so the word "Account" is dropped here
  // ("Account activated" → "Activated"). The customer page keeps the full label — there the
  // badge has no heading to lean on. Same variants either way.
  const label = a.label.replace(/^Account /, '').replace(/^./, (c) => c.toUpperCase());
  return (
    <Badge variant={a.variant} data-testid={testId}>
      {label}
    </Badge>
  );
}

/** Stripe status of the customer's most urgent subscription; "+" when there is more than one. */
export function SubscriptionStatusBadge({
  status,
  multiple,
  testId,
}: {
  status?: string | null;
  multiple?: boolean | null;
  testId?: string;
}) {
  const b = subscriptionBadge(status);
  if (!b) {
    return (
      <span className="text-muted-foreground" data-testid={testId}>
        {'No subscription'}
      </span>
    );
  }
  return (
    <Badge
      variant={b.variant}
      title={multiple ? 'More than one subscription — showing the most urgent' : undefined}
      data-testid={testId}
    >
      {/* The column is headed "Subscription status", so "Subscription" is dropped here
          ("Subscription past due" → "Past due"); the customer page keeps the full label. */}
      {b.label.replace(/^Subscription /, '').replace(/^./, (c) => c.toUpperCase())}
      {multiple ? ' +' : ''}
    </Badge>
  );
}

/**
 * The Billing Console dunning flag — Stripe metadata `staff-app-dunning-exclusion`:
 *   'true'  → Stopped (red)
 *   'pause' → Paused until <date> (amber), or "Paused (no end date)"
 *   anything else ('false', unset, hand-typed) → "—" (not excluded)
 * The same badge in the review queue and the Dunning Exclusion List.
 */
export function DunningFlagBadge({
  value,
  untilDate,
  testId,
}: {
  value?: string | null;
  untilDate?: string | null;
  testId?: string;
}) {
  if (value === 'true') {
    return (
      <Badge variant="destructive" data-testid={testId}>
        {'Stopped'}
      </Badge>
    );
  }
  if (value === 'pause') {
    return (
      <Badge variant="warning" data-testid={testId}>
        {untilDate ? `Paused until ${dayLabel(untilDate)}` : 'Paused (no end date)'}
      </Badge>
    );
  }
  return (
    <span className="text-muted-foreground" data-testid={testId}>
      {'—'}
    </span>
  );
}
